import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

import { trackBook } from "@/analytics";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Blog", href: "/blog", isRoute: true },
  { label: "Marion", href: "/marion", isRoute: true },
  { label: "Black Mountain", href: "/black-mountain", isRoute: true },
  { label: "Contact", href: "#contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-header shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <a href="#" className="font-heading text-2xl lg:text-3xl font-semibold text-[#3A4F41] tracking-tight" data-testid="logo-link">
          Dr. Greenspan
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                className="font-body text-sm tracking-wide text-[#5C6A61] hover:text-[#3A4F41] transition-colors duration-200"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                className="font-body text-sm tracking-wide text-[#5C6A61] hover:text-[#3A4F41] transition-colors duration-200"
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </a>
            )
          ))}
          <Link to="/book-online" onClick={trackBook}>
            <Button
              data-testid="header-book-btn"
              className="rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body text-sm px-6 h-10"
            >
              Book Online
            </Button>
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 text-[#3A4F41]"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden glass-header px-6 pb-6 animate-fade-in" data-testid="mobile-menu">
          <nav className="flex flex-col gap-4">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="font-body text-base text-[#5C6A61] hover:text-[#3A4F41] transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="font-body text-base text-[#5C6A61] hover:text-[#3A4F41] transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </a>
              )
            ))}
            <Link to="/book-online" onClick={() => { trackBook(); setMobileOpen(false); }}>
              <Button
                data-testid="mobile-book-btn"
                className="rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body w-full mt-2"
              >
                Book Online
              </Button>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
